import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ProgressTracker from '@ui-parts/ProgressTracker';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import { Box, Tab, Tabs } from '@mui/material';
import {
  LOCAL_STORAGES,
  PROGRESS_TRACKER_LABELS,
  PROGRESS_TYPE,
  SURVEY_PROGRESSES,
} from '~/constants';
import { SURVEY_REGISTER_TAB } from '~/common/constants';
import { ROUTES } from '~/constants/routes';
import PageLinkBar from '~/components/ui-parts/PageLinkBar';
import { BASE_FONT_FAMILY, FONT_SIZES, PALETTE } from '~/assets/styles/base';

import { createOriginalForm, getPreviewFinalize, copyOriginalForm, getPastSurveys } from '~/apiTs';
import { GMOASK_STATUS } from '~/constants/api';
import { useAppDispatch } from '~/App/hooks';
import {
  CreateOriginalFormResponse,
  PreviewSurveyResponse,
  OriginalFormCopyResponse,
  PastSurveyResponse,
  PastSurveyData,
} from '~/common/types/api/users';

import RegisterSurveyTab from './organisms/RegisterSurveyTab';
import PastSurveyTab from './organisms/PastSurveyTab';

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 100%;
  text-align: left;
  font-size: ${FONT_SIZES.title1};
  margin-bottom: 35px;
  gap: 10px;
`;

const Title = styled(Typography)`
  font-size: ${FONT_SIZES.title1};
`;

const SurveyRegisterForm = styled.form`
  width: 960px;
  margin: auto;
`;

const SurveyRegisterOriginalForm: React.FC<Record<string, never>> = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [pastSuveyData, setPastSurveyData] = useState<Array<PastSurveyData>>([]);

  const links = [
    {
      label: `${t('pageTitles.user.surveyRegisterSelection.category')}   ${t(
        'pageTitles.user.surveyRegisterSelection.title'
      )}`,
      routePath: ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION,
    },
    {
      // For arrow to enable on display
      label: '',
      routePath: ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION,
    },
  ];

  const registerForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      surveyName: '',
    },
  });

  const handleChangeSurveyTitle = (e: ChangeEvent<HTMLInputElement>) => {
    registerForm.setValue('surveyName', e.target.value);
  };

  const handleBlurSurveyTitle = (e: FocusEvent<HTMLInputElement>) => {
    registerForm.setValue('surveyName', e.target.value.trim());
  };

  const handleOnEnterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      registerForm.setValue('surveyName', target.value.trim());
    }
  };

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async () => {
    const { payload: response } = (await dispatch(
      createOriginalForm({
        name: registerForm.getValues('surveyName'),
      })
    )) as unknown as PayloadAction<
      CreateOriginalFormResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (response.status === GMOASK_STATUS.OK) {
      const originalFormId = response.result.surveyId;
      const originalFormName = registerForm.getValues('surveyName');

      if (originalFormId !== null && originalFormName !== null) {
        dispatch({
          type: 'originalForm/updated',
          payload: { name: originalFormName, id: originalFormId },
        });

        localStorage.setItem(LOCAL_STORAGES.ORIGINAL_FORM.valueOf(), originalFormName);

        navigate(
          ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM_MANUAL.replace(':id', originalFormId.toString())
        );
      }
    }
  };

  const showPreviewPage = async (miniEnqueteId: number) => {
    const { payload: previewContent } = (await dispatch(
      getPreviewFinalize({
        surveyId: miniEnqueteId,
      })
    )) as unknown as PayloadAction<
      PreviewSurveyResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (previewContent.status === GMOASK_STATUS.OK) {
      const { redirectUrl } = previewContent.result;
      window.open(redirectUrl, '_blank')?.focus();
    }
  };

  const miniEnqueteCopySurvey = async (miniEnqueteId: number) => {
    const { payload: previewContent } = (await dispatch(
      copyOriginalForm({ miniEnqueteId })
    )) as unknown as PayloadAction<
      OriginalFormCopyResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (previewContent.status === GMOASK_STATUS.OK) {
      navigate(
        ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM_MANUAL.replace(
          ':id',
          previewContent.result.surveyId.toString()
        )
      );
    }
  };
  const getPastSurvey = async () => {
    const { payload: pastSurveyResponse } = (await dispatch(
      getPastSurveys()
    )) as unknown as PayloadAction<
      PastSurveyResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;
    if (pastSurveyResponse.status === GMOASK_STATUS.OK) {
      setPastSurveyData(pastSurveyResponse.result);
    }
  };

  useEffect(() => {
    getPastSurvey();
  }, []);
  return (
    <>
      <ProgressTracker
        width="618px"
        labels={(PROGRESS_TRACKER_LABELS as Record<string, Array<string>>)[
          PROGRESS_TYPE.SURVEY
        ].map((label) => t(label))}
        currentNum={SURVEY_PROGRESSES[ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION]}
      />

      <SurveyRegisterForm
        data-testid="surveyRegisterForm"
        onSubmit={registerForm.handleSubmit(onSubmit)}
        noValidate
      >
        {links !== null && <PageLinkBar links={links} />}
        <TitleWrapper>
          <Box
            data-testid="google-form-label-box"
            sx={{
              fontSize: '14px',
              width: '164px',
              height: '28px',
              padding: '5px 18px',
              border: `1px solid ${PALETTE.border.frame}`,
              background: '#FFFFFF',
              color: PALETTE.text.main,
              fontFamily: BASE_FONT_FAMILY,
            }}
          >
            {t('survey.originalFormLabel')}
          </Box>
          <Title>{t('pageTitles.user.surveyRegisterOriginalForm.title')}</Title>
        </TitleWrapper>
        <Box>
          <Tabs
            aria-label="original_form_tabs"
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-flexContainer ': {
                display: 'block',
                float: 'left',
                width: '100%',
                borderBottom: `1px solid ${PALETTE.border.frame}`,
              },
              '& .MuiTab-root': {
                fontSize: '16px',
                color: `${PALETTE.text.main}`,
                width: '224px',
                height: '64px',
                marginBottom: '-1px',
              },
              '& .Mui-selected': {
                backgroundColor: `${PALETTE.white}`,
                borderLeft: `1px solid ${PALETTE.border.frame}`,
                borderRight: `1px solid ${PALETTE.border.frame}`,
                borderTopLeftRadius: '5px',
                borderTopRightRadius: '5px',
                fontWeight: 'bold',
                borderBottom: 'none',
                color: `${PALETTE.text.main} !important`,
              },
              '& .MuiTabs-indicator': {
                top: '0',
                height: '6px',
                borderTopLeftRadius: '5px',
                borderTopRightRadius: '5px',
              },
            }}
          >
            <Tab
              label={t('registration.originalFormTab.surveyCreate')}
              value={SURVEY_REGISTER_TAB.REGISTER}
              data-testid="tab_survey_create"
            />
            <Tab
              label={t('registration.originalFormTab.surveyPast')}
              value={SURVEY_REGISTER_TAB.REGISTER_PAST}
              data-testid="tab_past_survey"
            />
          </Tabs>
        </Box>
        {selectedTab === SURVEY_REGISTER_TAB.REGISTER && (
          <RegisterSurveyTab
            handleChangeSurveyTitle={handleChangeSurveyTitle}
            handleBlurSurveyTitle={handleBlurSurveyTitle}
            handleOnEnterKeyDown={handleOnEnterKeyDown}
            registerForm={registerForm}
          />
        )}
        {selectedTab === SURVEY_REGISTER_TAB.REGISTER_PAST && (
          <PastSurveyTab
            data={pastSuveyData}
            showPreviewPage={showPreviewPage}
            buttonCreate={miniEnqueteCopySurvey}
          />
        )}
      </SurveyRegisterForm>
    </>
  );
};

export default SurveyRegisterOriginalForm;
